import { useState } from 'react';
import { useTranslate } from 'react-admin';
import {
  Box,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  IconButton,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Collapse,
  Chip,
  Tooltip,
  useMediaQuery,
  useTheme,
  type SelectChangeEvent,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import SortIcon from '@mui/icons-material/Sort';
import InboxIcon from '@mui/icons-material/Inbox';
import PersonIcon from '@mui/icons-material/Person';
import {
  ConversationFilter,
  type ConversationFiltersProps,
  type AssignmentFilter,
} from '../types/tickets.types';
import { TicketStatus, TicketPriority } from '../../../types/chat.types';

/**
 * ConversationFilters Component
 *
 * Provides comprehensive filtering capabilities for the conversation list including:
 * - Tab-based quick filters (All, Mine, Open Tickets) - reduced from 5 to 3
 * - Search input for message content
 * - Assignment dropdown (replaces Unassigned tab)
 * - Archived toggle (replaces Archived tab)
 * - Dropdown filters for status, priority, queue, and channel
 * - Responsive design with compact mode for smaller screens
 */
export function ConversationFilters({
  filterState,
  onFilterChange,
  onTabChange,
  onSearchChange,
  onSortChange,
  sortBy = 'lastActivityAt',
  availableQueues = [],
  availableChannels = [],
  className,
}: ConversationFiltersProps) {
  const translate = useTranslate();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('lg'));
  const isVeryCompact = useMediaQuery(theme.breakpoints.down('md'));
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Tab configuration - 2 essential tabs
  const tabs = [
    {
      value: ConversationFilter.ALL,
      label: translate('resources.conversations.tabs.all'),
      icon: <InboxIcon fontSize="small" />,
    },
    {
      value: ConversationFilter.MINE,
      label: translate('resources.conversations.tabs.mine'),
      icon: <PersonIcon fontSize="small" />,
    },
  ];

  const handleTabChange = (_event: React.SyntheticEvent, newValue: ConversationFilter) => {
    onTabChange(newValue);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    onSearchChange(query);
  };

  const handleClearSearch = () => {
    onSearchChange('');
  };

  const handleAssignmentChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value as AssignmentFilter;
    onFilterChange({ assignment: value === 'all' ? undefined : value });
  };

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    onFilterChange({ status: value ? (value as TicketStatus) : null });
  };

  const handlePriorityChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    onFilterChange({ priority: value ? (value as TicketPriority) : null });
  };

  const handleQueueChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    onFilterChange({ queueUuid: value || null });
  };

  const handleChannelChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    onFilterChange({ channelUuid: value || null });
  };

  const toggleAdvancedFilters = () => {
    setShowAdvancedFilters((prev) => !prev);
  };

  // Check if any advanced filters are active
  const activeFilterCount = [
    filterState.options.status,
    filterState.options.priority,
    filterState.options.queueUuid,
    filterState.options.channelUuid,
    filterState.options.assignment,
  ].filter(Boolean).length;

  return (
    <Box className={className}>
      {/* Filter Tabs - reduced to 3 */}
      <Tabs
        value={filterState.activeFilter}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: 44,
          '& .MuiTab-root': {
            minHeight: 44,
            py: 1,
          },
        }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={
              isVeryCompact ? (
                <Tooltip title={tab.label}>
                  <Box display="flex" alignItems="center" justifyContent="center">
                    {tab.icon}
                  </Box>
                </Tooltip>
              ) : (
                <Box display="flex" alignItems="center" gap={0.5}>
                  {isCompact && tab.icon}
                  <span>{tab.label}</span>
                </Box>
              )
            }
            sx={{
              textTransform: 'none',
              fontSize: isCompact ? '0.8rem' : '0.875rem',
              fontWeight: 500,
              minWidth: 0,
              px: isCompact ? 1 : 2,
            }}
          />
        ))}
      </Tabs>

      {/* Search Input */}
      <Box sx={{ p: 1.5, pb: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          fullWidth
          size="small"
          placeholder={translate('resources.conversations.filters.search')}
          value={filterState.options.search || ''}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: filterState.options.search && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  edge="end"
                  aria-label={translate('ra.action.clear_input_value')}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'background.default',
              height: 36,
            },
          }}
        />
      </Box>

      {/* Sort + Filter Toggle Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          py: 0.5,
          gap: 1,
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        {/* Sort dropdown */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={sortBy}
            onChange={(e: SelectChangeEvent) => onSortChange?.(e.target.value as 'lastActivityAt' | 'createdAt')}
            startAdornment={<SortIcon fontSize="small" sx={{ mr: 0.5, color: 'action.active' }} />}
            sx={{ fontSize: '0.8rem', height: 32 }}
          >
            <MenuItem value="lastActivityAt">{translate('resources.conversations.sort.lastActivity')}</MenuItem>
            <MenuItem value="createdAt">{translate('resources.conversations.sort.createdDate')}</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ flex: 1 }} />

        {/* Filter Toggle Button */}
        <Tooltip title={translate('resources.conversations.filters.advanced')}>
          <IconButton
            onClick={toggleAdvancedFilters}
            size="small"
            color={activeFilterCount > 0 ? 'primary' : 'default'}
            sx={{
              bgcolor: showAdvancedFilters ? 'action.selected' : 'transparent',
              position: 'relative',
            }}
          >
            <FilterListIcon fontSize="small" />
            {activeFilterCount > 0 && (
              <Chip
                label={activeFilterCount}
                size="small"
                color="primary"
                sx={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  height: 16,
                  minWidth: 16,
                  fontSize: 10,
                  '& .MuiChip-label': { px: 0.5 },
                }}
              />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Advanced Filters - Collapsible */}
      <Collapse in={showAdvancedFilters}>
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          <Grid container spacing={1.5}>
            {/* Assignment Filter (replaces Unassigned tab) */}
            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>
                  {translate('resources.conversations.filters.assignment')}
                </InputLabel>
                <Select
                  value={filterState.options.assignment || 'all'}
                  onChange={handleAssignmentChange}
                  label={translate('resources.conversations.filters.assignment')}
                  sx={{ fontSize: '0.8rem', height: 36 }}
                >
                  <MenuItem value="all">
                    {translate('resources.conversations.filters.assignmentAll')}
                  </MenuItem>
                  <MenuItem value="mine">
                    {translate('resources.conversations.filters.assignmentMine')}
                  </MenuItem>
                  <MenuItem value="unassigned">
                    {translate('resources.conversations.filters.assignmentUnassigned')}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Status Filter */}
            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>
                  {translate('resources.conversations.filters.status')}
                </InputLabel>
                <Select
                  value={filterState.options.status || ''}
                  onChange={handleStatusChange}
                  label={translate('resources.conversations.filters.status')}
                  sx={{ fontSize: '0.8rem', height: 36 }}
                >
                  <MenuItem value="">
                    <em>{translate('ra.action.clear_input_value')}</em>
                  </MenuItem>
                  <MenuItem value={TicketStatus.OPEN}>
                    {translate('resources.ticket.status.open')}
                  </MenuItem>
                  <MenuItem value={TicketStatus.RESOLVED}>
                    {translate('resources.ticket.status.resolved')}
                  </MenuItem>
                  <MenuItem value={TicketStatus.CLOSED}>
                    {translate('resources.ticket.status.closed')}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Priority Filter */}
            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>
                  {translate('resources.conversations.filters.priority')}
                </InputLabel>
                <Select
                  value={filterState.options.priority || ''}
                  onChange={handlePriorityChange}
                  label={translate('resources.conversations.filters.priority')}
                  sx={{ fontSize: '0.8rem', height: 36 }}
                >
                  <MenuItem value="">
                    <em>{translate('ra.action.clear_input_value')}</em>
                  </MenuItem>
                  <MenuItem value={TicketPriority.LOW}>
                    {translate('resources.ticket.priority.low')}
                  </MenuItem>
                  <MenuItem value={TicketPriority.MEDIUM}>
                    {translate('resources.ticket.priority.medium')}
                  </MenuItem>
                  <MenuItem value={TicketPriority.HIGH}>
                    {translate('resources.ticket.priority.high')}
                  </MenuItem>
                  <MenuItem value={TicketPriority.URGENT}>
                    {translate('resources.ticket.priority.urgent')}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Queue Filter */}
            {availableQueues.length > 0 && (
              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.8rem' }}>
                    {translate('resources.conversations.filters.queue')}
                  </InputLabel>
                  <Select
                    value={filterState.options.queueUuid || ''}
                    onChange={handleQueueChange}
                    label={translate('resources.conversations.filters.queue')}
                    sx={{ fontSize: '0.8rem', height: 36 }}
                  >
                    <MenuItem value="">
                      <em>{translate('ra.action.clear_input_value')}</em>
                    </MenuItem>
                    {availableQueues.map((queue) => (
                      <MenuItem key={queue.uuid} value={queue.uuid}>
                        {queue.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Channel Filter */}
            {availableChannels.length > 0 && (
              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.8rem' }}>
                    {translate('resources.conversations.filters.channel')}
                  </InputLabel>
                  <Select
                    value={filterState.options.channelUuid || ''}
                    onChange={handleChannelChange}
                    label={translate('resources.conversations.filters.channel')}
                    sx={{ fontSize: '0.8rem', height: 36 }}
                  >
                    <MenuItem value="">
                      <em>{translate('ra.action.clear_input_value')}</em>
                    </MenuItem>
                    {availableChannels.map((channel) => (
                      <MenuItem key={channel.uuid} value={channel.uuid}>
                        {channel.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
}
